import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import axios from "axios";
import {
  Eye, MousePointer, ShoppingCart, DollarSign, TrendingUp, Copy, Check, Plus,
  Youtube, Search, MessageCircle, ThumbsUp, BarChart3, Loader2, ArrowUpRight, Clock,
  Crosshair, Trophy, Sparkles, ArrowUp, ArrowDown, Minus, AlertTriangle, ChevronRight,
  Crosshair as CrosshairIcon, RefreshCw, LogOut as DisconnectIcon, Radio,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Textarea } from "./components/ui/textarea";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { CompetitorCard } from "./CompetitorCard";
import { NicheReviewCard } from "./NicheReviewCard";
import { TrendingPanel } from "./TrendingPanel";
import { SwipeFileScreen, useSwipeFile } from "./SwipeFile";
import { Sidebar } from "./Sidebar";
import { UtmBuilder, InlineUtmCell, useUtmSettings, useUtmOverrides, buildUtmLink } from "./UtmBuilder";
import { OverlapChart } from "./OverlapChart";
import { TimeRangePicker } from "./TimeRangePicker";
import { PixelStatus } from "./PixelStatus";
import { Login } from "./Login";
import { SharedHookPage } from "./SharedHookPage";
import { PrivacyPage, TermsPage } from "./Legal";
import { PricingScreen, UpgradeGate, BillingBadge } from "./Pricing";
import { OfferManager } from "./Offers";
import { OnboardingWizard, useOnboarding } from "./Onboarding";
import { useYouTubeStatus } from "./InjectLink";
import { IntegrationsScreen } from "./Integrations";
import { VideoTable as VideoTableImpl } from "./VideoTable";
import { SiteFooter } from "./SiteFooter";
import { AdminScreen } from "./Admin";
import { AskAiScreen, AskAiBubble } from "./AiChat";
import MarketingHomepage from "./Marketing";
import DashboardHome from "./DashboardHome";
import { UsageProvider, UsageBanner, UsageCapModal, UsageMeter, useUsage, handleUsageCapError } from "./UsageCaps";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth: rely on the httpOnly session cookie set by /api/auth/session
axios.defaults.withCredentials = true;

const formatNumber = (num) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return String(num ?? 0);
};

const formatCurrency = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function mapRangeToDays(tr) {
  // Map the new TimeRangePicker presets into the legacy /api/videos `days` filter
  switch (tr.range) {
    case "this_week": case "last_week": case "last_7": return { days: 7 };
    case "this_month": case "last_month": case "last_30": return { days: 30 };
    case "last_90": return { days: 90 };
    case "all_time": return {};
    case "custom": {
      if (tr.start && tr.end) {
        const ms = new Date(tr.end) - new Date(tr.start);
        return { days: Math.max(1, Math.round(ms / 86_400_000) + 1) };
      }
      return {};
    }
    default: return { days: 30 };
  }
}

// UsageSummaryCard — thin wrapper that pulls current-user usage from the
// UsageProvider context and renders it as a card at the top of the Integrations
// (Settings) screen. Kept in App.js so it can reach the App-scoped
// `openPricing` callback via a prop.
function UsageSummaryCard({ onUpgrade }) {
  const { usage, loading } = useUsage();
  if (loading || !usage) return null;
  const hitAny = Object.values(usage.usage || {}).some((r) => !r.unlimited && r.pct_used >= 80);
  return (
    <div className="mb-8 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6" data-testid="usage-summary-card">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Fair-use meter</div>
          <div className="text-white font-semibold text-lg">Your usage this month</div>
          <p className="text-sm text-zinc-400 mt-0.5 max-w-lg">
            Every plan has a monthly cap on the AI-powered features. Resets on the 1st. Upgrade any time — no proration surprises.
          </p>
        </div>
        {usage.plan !== "pro" && (
          <button
            type="button"
            onClick={onUpgrade}
            className={`px-3.5 h-9 rounded-lg text-sm font-medium border transition-colors ${
              hitAny
                ? "bg-red-600 hover:bg-red-500 text-white border-red-400/30 shadow-[0_0_14px_rgba(255,0,0,0.28)]"
                : "bg-white/[0.04] hover:bg-white/[0.08] text-zinc-200 border-white/10"
            }`}
            data-testid="usage-summary-upgrade-btn"
          >
            Upgrade to Pro
          </button>
        )}
      </div>
      <UsageMeter usage={usage} />
    </div>
  );
}


function Dashboard({ user, onSignOut }) {
  const [activeView, setActiveView] = useState("home");
  const [billing, setBilling] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  // Live-tracking heartbeat — drives the pulsing green dot on the channel pill
  // when the pixel has fired in the last 5 minutes. Polls every 30s. Cheap
  // server-side (2 count_documents + 1 find_one each call).
  const [heartbeat, setHeartbeat] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/tracking/heartbeat`);
        if (!cancelled) setHeartbeat(data);
      } catch (e) {
        // Heartbeat is purely cosmetic — silence errors so a transient 5xx
        // doesn't spam the console while the user is mid-task.
      }
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const { status: onboarding, refresh: refreshOnboarding } = useOnboarding(true);
  const { savedSet: swipeSavedSet, markSaved: markSwipeSaved, refresh: refreshSwipeFile } = useSwipeFile();

  // Surface the wizard automatically for paid users who haven't finished setup yet.
  useEffect(() => {
    if (!billing || !onboarding) return;
    if (billing.plan === "free") return;        // free users see the upgrade flow first
    if (onboarding.completed) return;            // user already dismissed it
    const allDone = onboarding.youtube_oauth_connected && onboarding.has_offer && onboarding.pixel_installed;
    if (!allDone) setShowWizard(true);
  }, [billing, onboarding]);

  const fetchBilling = async () => {
    try {
      const { data } = await axios.get(`${API}/billing/me`);
      setBilling(data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { fetchBilling(); }, []);

  // Surface Stripe redirect on first load (success / canceled in query string)
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("billing") === "success" || qs.get("billing") === "canceled") {
      setShowPricing(true);   // Pricing page handles the polling + cleans the URL
    }
  }, []);

  const openPricing = () => setShowPricing(true);
  const closePricing = () => setShowPricing(false);
  const handlePurchased = () => {
    fetchBilling();
    setShowPricing(false);
    toast.success("Plan unlocked — let's go!");
  };

  // One-click trial — used by the dashboard banner and every UpgradeGate.
  // Skips the pricing page entirely: straight to Stripe checkout with
  // trial_days=14. The smoother this is, the higher the trial-start rate.
  const startFreeTrial = async () => {
    try {
      // Partnero affiliate attribution — read the cookie Partnero's Universal
      // JS drops when a visitor lands via an affiliate link, forward to Stripe.
      const partnerMatch = document.cookie.match(/(?:^|;\s*)partnero_partner=([^;]+)/);
      const partnerKey = partnerMatch ? decodeURIComponent(partnerMatch[1]) : null;
      const payload = {
        plan_id: "pro",
        origin_url: window.location.origin,
        trial_days: 14,
      };
      if (partnerKey) payload.partner_key = partnerKey;
      const { data } = await axios.post(`${API}/billing/checkout`, payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Couldn't start trial");
      toast.error(msg);
    }
  };
  const trialEligible = billing?.plan === "free" && billing?.trial_used === false;

  const openBillingPortal = async () => {
    try {
      const { data } = await axios.post(`${API}/billing/portal`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal");
    }
  };

  const isPaid = billing?.plan && billing.plan !== "free";

  const [summary, setSummary] = useState(null);   // { range_label, totals, deltas, series, ... }
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [timeRange, setTimeRange] = useState({ range: "last_30" });
  const [loading, setLoading] = useState(true);

  const [pixel, setPixel] = useState(null);
  const [pixelLoading, setPixelLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  // UTM settings + per-video overrides are scoped per user_id to prevent
  // one account's offer URL from leaking into the next account that signs
  // in on the same browser (the bug Tim found Feb 2026).
  const userId = user?.user_id || user?.email || null;
  const [utmSettings, setUtmSettings] = useUtmSettings(userId);
  const [utmOverrides, setUtmOverride] = useUtmOverrides(userId);
  // YouTube OAuth status (used by the per-row "Inject link" button) — fetched
  // once at the dashboard level and passed down to avoid N+1 status calls.
  const { status: ytStatus, reauthorize: reauthorizeYouTube } = useYouTubeStatus();

  const [connectOpen, setConnectOpen] = useState(false);
  const [newChannelUrl, setNewChannelUrl] = useState("");
  const [connecting, setConnecting] = useState(false);

  const [competitorVideos, setCompetitorVideos] = useState([]);
  const [nicheReview, setNicheReview] = useState(null);
  const [nicheReviewLoading, setNicheReviewLoading] = useState(false);
  const [competitorLoading, setCompetitorLoading] = useState(false);
  const [competitorChannelName, setCompetitorChannelName] = useState("");

  useEffect(() => { fetchData(); }, [timeRange]); // eslint-disable-line
  useEffect(() => {
    if (activeView === "tracking" && !pixel && !pixelLoading) fetchPixel();
  }, [activeView]); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const summaryParams = { range: timeRange.range };
      if (timeRange.range === "custom") {
        summaryParams.start = timeRange.start;
        summaryParams.end = timeRange.end;
      }
      // For per-video table we keep using existing /videos endpoint with a days hint
      const videoParams = mapRangeToDays(timeRange);
      const [sum, vids, chans] = await Promise.all([
        axios.get(`${API}/dashboard/summary`, { params: summaryParams }),
        axios.get(`${API}/videos`, { params: videoParams }),
        axios.get(`${API}/channels`),
      ]);
      setSummary(sum.data);
      setVideos(vids.data);
      setChannels(chans.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Show full-screen loader only on the very first fetch. Subsequent refreshes
  // (e.g. switching time range) happen in the background — the UI keeps the
  // previous data so the user never sees a flash of empty screen.
  // -------------------------------------------------------------------------
  const initialLoading = loading && !summary;

  const fetchPixel = async () => {
    if (pixel) return;
    setPixelLoading(true);
    try {
      const { data } = await axios.get(`${API}/tracking/pixel`);
      setPixel(data);
    } catch (e) {
      toast.error("Failed to fetch tracking pixel");
    } finally {
      setPixelLoading(false);
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success(`${key} copied`);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  const connectChannel = async () => {
    if (!newChannelUrl.trim()) {
      toast.error("Enter a YouTube channel URL or @handle");
      return;
    }
    setConnecting(true);
    try {
      await axios.post(`${API}/channels`, { channel_url: newChannelUrl });
      toast.success("Channel connected");
      setNewChannelUrl("");
      setConnectOpen(false);
      fetchData();
    } catch (e) {
      if (e?.response?.status === 402) {
        setConnectOpen(false);
        setShowPricing(true);
        toast.info("Subscribe to connect a channel");
      } else {
        // Surface the backend's actual error so the user (and we) can see what
        // failed — generic "Failed to connect" hides quota errors, URL parse
        // failures, missing channels, etc.
        const detail = e?.response?.data?.detail;
        const message = typeof detail === "string"
          ? detail
          : (detail?.message || e?.message || "Failed to connect channel");
        toast.error(`Couldn't connect channel: ${message}`);
        console.error("connect_channel failed", e?.response?.status, e?.response?.data);
      }
    } finally {
      setConnecting(false);
    }
  };

  const disconnectChannel = async (channelId) => {
    // If this is the YouTube-OAuth-connected channel, the user almost
    // certainly means "disconnect the YouTube account from VidROI"  — not
    // just remove the row (which would just re-auto-register on the next
    // page load from the active OAuth token). Run the full OAuth disconnect
    // in that case so tokens + cached state are purged together.
    const oauthChannelId = onboarding?.youtube_oauth_connected
      ? (channels.find((c) => c.connected_via === "oauth" && c.channel_id === channelId)?.channel_id)
      : null;
    const isOAuthChannel = Boolean(oauthChannelId);
    try {
      if (isOAuthChannel) {
        await axios.delete(`${API}/youtube/oauth/disconnect`);
        toast.success("YouTube disconnected");
      } else {
        await axios.delete(`${API}/channels/${channelId}`);
        toast.success("Channel disconnected");
      }
      // Reload onboarding + channels so the UI reflects the new state immediately
      await Promise.all([fetchData(), refreshOnboarding()]);
    } catch (e) {
      toast.error(isOAuthChannel ? "Couldn't disconnect YouTube" : "Failed to disconnect channel");
    }
  };

  const analyzeCompetitor = async () => {
    if (!competitorChannelName.trim()) {
      toast.error("Enter a competitor channel name");
      return;
    }
    setCompetitorLoading(true);
    setNicheReview(null);
    // Kick off the channel-level Claude niche review in parallel with the top-5
    // video fetch. It analyses 50 videos so it takes ~10s, but shows independently
    // above the video cards so the user isn't blocked.
    setNicheReviewLoading(true);
    axios
      .post(`${API}/competitors/niche-review`, null, { params: { channel_name: competitorChannelName, max_videos: 50 } })
      .then(({ data }) => setNicheReview(data))
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        const isCapHit = e?.response?.status === 429 && detail && typeof detail === "object" && detail.code === "usage_cap_hit";
        if (!isCapHit) {
          // 402 (needs paid plan) — silent; the video-fetch path already surfaces upgrade prompts.
          if (e?.response?.status !== 402) {
            console.warn("[niche-review] failed:", e?.response?.data || e?.message);
          }
        }
      })
      .finally(() => setNicheReviewLoading(false));
    try {
      // STEP 1 — Fetch the 5 videos WITHOUT transcripts. This is fast (<3s)
      // because it skips the yt-dlp + Whisper work that used to run inside
      // the request and blow past Cloudflare's 100s timeout, producing 520
      // "origin returned invalid response" errors.
      const { data } = await axios.get(`${API}/competitors/videos`, {
        params: { channel_name: competitorChannelName, transcribe: false },
      });
      setCompetitorVideos(data);
      toast.success(`Found top ${data.length} videos — transcribing hooks…`);
      setCompetitorLoading(false);

      // STEP 2 — Lazy-fetch each transcript IN PARALLEL from the browser.
      // Each request is its own HTTP call → none of them can individually
      // exceed Cloudflare's timeout, and the UI fills in as each finishes
      // (better perceived perf than a 60s monolithic wait).
      data.forEach((v, idx) => {
        if (!v.video_id || !v.video_url) return;
        axios.post(`${API}/competitors/transcribe/${v.video_id}`, null, {
          params: { youtube_url: v.video_url },
        })
          .then((resp) => {
            const transcript = resp.data?.transcript || "";
            const analysis = resp.data?.analysis || null;
            setCompetitorVideos((current) => {
              const next = [...current];
              if (next[idx] && next[idx].video_id === v.video_id) {
                next[idx] = {
                  ...next[idx],
                  // Field name kept for back-compat with shared-hook page;
                  // value is now the full 300-word transcript.
                  transcript_first_150_words: transcript,
                  transcript_full_300_words: transcript,
                  transcript_word_count: transcript ? transcript.split(/\s+/).length : 0,
                  analysis: analysis,
                };
              }
              return next;
            });
          })
          .catch((err) => {
            // Single-video transcript failures are non-fatal — leave the
            // transcript empty and let the user click "Watch video" to
            // see the original. We deliberately don't toast each failure
            // because 1-2 missing transcripts out of 5 is normal (deleted
            // audio, age-gated, geo-blocked, etc.).
            console.warn(`Transcript failed for ${v.video_id}:`, err?.response?.data?.detail || err.message);
          });
      });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Coerce object detail → string so React doesn't crash inside toast.error.
      // (429 usage_cap_hit is caught by the global interceptor before it lands
      // here — but this endpoint returns 429 for YouTube quota exhaustion too,
      // which uses a plain-string detail.)
      const detailStr = typeof detail === "string" ? detail : (detail?.message || null);
      if (status === 429) {
        toast.error(detailStr || "YouTube API quota exhausted — try again tomorrow.");
      } else {
        toast.error(detailStr || "Failed to analyze competitor channel");
      }
      setCompetitorLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Section views
  // -------------------------------------------------------------------------

  const renderHeader = (title, eyebrow, accentWord, action) => (
    <header className="flex items-end justify-between gap-6 mb-8 animate-fade-up">
      <div>
        {eyebrow && (
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">
            {eyebrow}
          </div>
        )}
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05]">
          {title}
          {accentWord && (
            <>
              {" "}
              <span className="text-red-500 drop-shadow-[0_0_18px_rgba(255,0,0,0.5)]">{accentWord}</span>
            </>
          )}
        </h1>
      </div>
      {action}
    </header>
  );

  const TimeRangeSelect = (
    <TimeRangePicker
      value={timeRange}
      onChange={setTimeRange}
      currentLabel={summary?.range_label}
      currentStart={summary?.range_start}
      currentEnd={summary?.range_end}
    />
  );

  const ConnectChannelDialog = (
    <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
      <DialogTrigger asChild>
        <Button
          className="bg-red-600 hover:bg-red-500 text-white shadow-[0_0_18px_rgba(255,0,0,0.35)] hover:shadow-[0_0_28px_rgba(255,0,0,0.55)] border border-red-400/30 font-medium"
          data-testid="connect-channel-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Connect Channel
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border border-white/10 text-zinc-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Connect a YouTube channel</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Paste a channel URL, an @handle, or even just the channel name.
          </DialogDescription>
        </DialogHeader>
        {/* Layman-friendly YouTube sign-in status — pre-empts the 403 error
            users would otherwise hit by trying to add a channel without being
            signed in. Channels can be ones the user owns OR channels they
            manage for someone else (agencies, coaches, etc.). */}
        {onboarding?.youtube_oauth_connected && onboarding?.youtube_channel_title ? (
          <div
            className="mt-3 flex items-start gap-3 p-3 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.05]"
            data-testid="add-channel-yt-status-connected"
          >
            <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-zinc-300 leading-relaxed">
              You're signed in to YouTube as <strong className="text-white">{onboarding.youtube_channel_title}</strong>.
              You can add any channel you own or manage — just paste its URL or @handle below.
            </div>
          </div>
        ) : (
          <div
            className="mt-3 flex items-start gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.06]"
            data-testid="add-channel-yt-status-disconnected"
          >
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-zinc-300 leading-relaxed flex-1">
              <div className="font-medium text-white mb-1">Sign in to YouTube first</div>
              We need to know who you are before adding channels. Sign in once and you can add any channel you own or manage.
              <button
                type="button"
                onClick={() => { setConnectOpen(false); setShowWizard(true); }}
                className="block mt-2 text-amber-300 hover:text-amber-200 underline underline-offset-2 font-medium"
                data-testid="add-channel-yt-connect-now"
              >
                Sign in to YouTube →
              </button>
            </div>
          </div>
        )}
        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="channel-url" className="text-zinc-300">Channel</Label>
            <Input
              id="channel-url"
              data-testid="channel-url-input"
              placeholder="@yourchannel"
              value={newChannelUrl}
              onChange={(e) => setNewChannelUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !connecting && onboarding?.youtube_oauth_connected && connectChannel()}
              disabled={!onboarding?.youtube_oauth_connected}
              className="bg-black/40 border-white/10 text-white mt-1 disabled:opacity-40 disabled:cursor-not-allowed"
            />
          </div>
          <Button
            onClick={connectChannel}
            disabled={connecting || !onboarding?.youtube_oauth_connected}
            className="w-full bg-red-600 hover:bg-red-500 text-white shadow-[0_0_18px_rgba(255,0,0,0.3)] border border-red-400/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            data-testid="connect-channel-submit"
          >
            {connecting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting</>) : "Connect"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // ----- DASHBOARD ---------------------------------------------------------
  const renderDashboard = () => {
    const primaryChannel = channels[0];
    const totals = summary?.totals || {};
    const deltas = summary?.deltas || {};
    return (
      <>
        {/* Compact header — channel + time range + Connect */}
        <header className="flex items-center justify-between gap-4 mb-6 animate-fade-up">
          <div className="flex items-center gap-4 min-w-0">
            {primaryChannel && (
              <img
                src={primaryChannel.thumbnail_url}
                alt={primaryChannel.channel_name}
                className="w-12 h-12 rounded-xl object-cover border border-white/10 shadow-[0_0_24px_rgba(255,0,0,0.15)] flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.2em] text-red-400/80 font-semibold">Overview</div>
              <div className="flex items-baseline gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white truncate">
                  {primaryChannel ? primaryChannel.channel_name : "Your channel"}
                </h1>
                {primaryChannel && (
                  <span className="text-sm text-zinc-500 hidden sm:inline">
                    {formatNumber(primaryChannel.subscriber_count)} subs · {primaryChannel.video_count} videos
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">{TimeRangeSelect}{ConnectChannelDialog}</div>
        </header>

        {!primaryChannel && (
          <div className="mb-6">
            {isPaid ? (
              <EmptyChannelCard onConnect={() => setConnectOpen(true)} />
            ) : (
              <UpgradeGate
                feature="Connect a YouTube channel"
                onUpgrade={openPricing}
                onTrial={startFreeTrial}
                trialEligible={trialEligible}
              />
            )}
          </div>
        )}

        {/* Stat cards row — Views / Clicks / Sales / Revenue with delta arrows */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard testid="metric-card-views"    icon={Eye}          label="Views"    value={formatNumber(totals.impressions)} delta={deltas.impressions} prevLabel={summary.previous_label} />
            <StatCard testid="metric-card-clicks"   icon={MousePointer} label="Clicks"   value={formatNumber(totals.clicks)}      delta={deltas.clicks}      prevLabel={summary.previous_label} />
            <StatCard testid="metric-card-sales"    icon={ShoppingCart} label="Sales"    value={formatNumber(totals.sales)}       delta={deltas.sales}       prevLabel={summary.previous_label} />
            <StatCard testid="metric-card-revenue"  icon={DollarSign}   label="Revenue"  value={formatCurrency(totals.revenue)}   delta={deltas.revenue}     prevLabel={summary.previous_label} accent />
          </div>
        )}

        {/* Overlap chart — all metrics on one normalized chart */}
        {summary && (
          <section className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl mb-8 animate-fade-up">
            <OverlapChart data={summary.series} rangeLabel={summary.range_label} />
          </section>
        )}

        {/* Top videos — primary table sits above the UTM builder so the
            date range is the first thing creators understand */}
        <SectionPanel
          icon={TrendingUp}
          title="Top videos"
          subtitle="Sorted by sales. Every row carries a ready-to-paste UTM link — drop it into the YouTube video description and you're tracking. Use the date selector at the top to change the period."
          badge={summary?.range_label ? `Showing · ${summary.range_label}` : null}
        >
          {/* Pointer to the relocated UTM Builder — the in-row UTM cell uses
              whatever template the builder produces, so creators who want to
              tweak the template need to know where it now lives. */}
          <button
            type="button"
            onClick={() => setActiveView("utm")}
            className="w-full mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] hover:bg-amber-500/[0.08] hover:border-amber-500/40 transition-colors px-3.5 py-2.5 text-left group"
            data-testid="video-table-utm-tip"
          >
            <div className="flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center text-xs font-bold">Σ</span>
              <div>
                <div className="text-sm text-white font-medium">Create UTM Link</div>
                <div className="text-xs text-zinc-400">Set the template once → every video row below auto-generates a ready-to-paste link.</div>
              </div>
            </div>
            <span className="text-amber-300 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
              Open UTM builder
              <ChevronRight className="w-4 h-4" />
            </span>
          </button>
          <VideoTable videos={videos} utmSettings={utmSettings} utmOverrides={utmOverrides} onOverrideChange={setUtmOverride} ytStatus={ytStatus} onReauthorize={reauthorizeYouTube} />
        </SectionPanel>
      </>
    );
  };

  // ----- TRACKING ----------------------------------------------------------
  const renderTracking = () => {
    return (
      <>
        {renderHeader("Universal", "Tracking", "pixel", TimeRangeSelect)}

        <div className="max-w-4xl space-y-6">
          <PixelStatus />

          <SectionPanel
            icon={Crosshair}
            title="One pixel. Every sale attributed."
            subtitle="Paste this snippet once on your site. It captures incoming UTM params, stores them for 30 days, fires a click event, and exposes window.trackYouTubeSale() for sale tracking."
          >
            {pixelLoading || !pixel ? (
              <div className="py-10 text-center text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading pixel…</div>
            ) : (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">1. Install snippet</Label>
                    <Button
                      size="sm"
                      onClick={() => copyToClipboard(pixel.pixel_code, "Pixel")}
                      className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_15px_rgba(255,0,0,0.3)]"
                      data-testid="copy-pixel-btn"
                    >
                      {copiedKey === "Pixel" ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                    </Button>
                  </div>
                  <Textarea
                    value={pixel.pixel_code}
                    readOnly
                    className="font-mono text-xs h-56 resize-none bg-[#09090b] border-white/10 text-zinc-300"
                    data-testid="pixel-code-textarea"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <InstructionBox
                    step="2"
                    title="Fire sales"
                    body={<>From your checkout / thank-you page, call:</>}
                    code="window.trackYouTubeSale(amount, transactionId)"
                  />
                  <InstructionBox
                    step="3"
                    title="Use per-video UTM links"
                    body="Grab them from the Dashboard's 'Copy UTM' button on any video row, then drop them in your YouTube descriptions."
                  />
                </div>
              </div>
            )}
          </SectionPanel>
        </div>
      </>
    );
  };

  // ----- COMPETITORS -------------------------------------------------------
  const renderCompetitors = () => (
    <>
      {renderHeader("Steal", "Research", "the hooks", null)}

      <SectionPanel
        icon={Search}
        title="Top 5 videos of any channel"
        subtitle="We'll pull the views, likes, comments, transcribe the 300-word opening hook, and let Claude break down the copywriting framework — so you can adapt it to your own niche."
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="Channel name, @handle, or URL — e.g. Ali Abdaal"
            value={competitorChannelName}
            onChange={(e) => setCompetitorChannelName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !competitorLoading && analyzeCompetitor()}
            className="bg-black/40 border-white/10 text-white flex-1"
            data-testid="competitor-search-input"
          />
          <Button
            onClick={analyzeCompetitor}
            disabled={competitorLoading || !competitorChannelName.trim()}
            className="bg-red-600 hover:bg-red-500 text-white shadow-[0_0_18px_rgba(255,0,0,0.35)] border border-red-400/30"
            data-testid="competitor-analyze-btn"
          >
            {competitorLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing</> : <><Sparkles className="w-4 h-4 mr-2" /> Analyze</>}
          </Button>
        </div>
      </SectionPanel>

      {(nicheReview || nicheReviewLoading) && (
        <div className="mt-8">
          <NicheReviewCard review={nicheReview} loading={nicheReviewLoading} />
          {/* Trending panel auto-fires once the niche is known. Silently
              hides itself if PERPLEXITY_API_KEY isn't configured. */}
          {nicheReview?.primary_niche && <TrendingPanel niche={nicheReview.primary_niche} />}
        </div>
      )}

      {competitorVideos.length > 0 && (
        <div className="mt-4 space-y-5 animate-fade-up">
          {competitorVideos.map((video, i) => (
            <CompetitorCard
              key={video.id}
              video={video}
              index={i}
              savedSet={swipeSavedSet}
              onSavedChange={(vid, saved) => { markSwipeSaved(vid, saved); refreshSwipeFile(); }}
            />
          ))}
        </div>
      )}

      {competitorVideos.length === 0 && !competitorLoading && (
        <div className="mt-10 text-center text-zinc-500 text-sm">
          <Trophy className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
          Pop in a competitor and we'll dissect their top hooks.
        </div>
      )}
    </>
  );

  // ----- CHANNELS ----------------------------------------------------------
  const renderChannels = () => (
    <>
      {renderHeader("Connected", "Channels", "channels", ConnectChannelDialog)}

      <div className="grid gap-4">
        {channels.length === 0 && <EmptyChannelCard onConnect={() => setConnectOpen(true)} />}
        {channels.map((ch) => (
          <div
            key={ch.channel_id}
            className="flex items-center gap-5 p-5 rounded-2xl bg-white/[0.02] border border-white/[0.08] hover:bg-white/[0.04] transition-colors"
            data-testid={`channel-card-${ch.channel_id}`}
          >
            <img src={ch.thumbnail_url} alt={ch.channel_name} className="w-14 h-14 rounded-xl object-cover border border-white/10" />
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium">{ch.channel_name}</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {formatNumber(ch.subscriber_count)} subs · {formatNumber(ch.total_views)} views · {ch.video_count} videos
              </div>
            </div>
            <a
              href={ch.channel_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-white text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10"
            >
              Open <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <Button
              variant="ghost"
              className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
              onClick={() => disconnectChannel(ch.channel_id)}
              data-testid={`disconnect-channel-btn-${ch.channel_id}`}
            >
              Disconnect
            </Button>
          </div>
        ))}
      </div>
    </>
  );

  // -------------------------------------------------------------------------
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-7 h-7 animate-spin text-red-500 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Loading your analytics…</p>
        </div>
      </div>
    );
  }

  // Pricing screen is a full-page overlay (modal-style takeover)
  if (showPricing) {
    return (
      <PricingScreen
        billing={billing}
        onClose={closePricing}
        onPurchased={handlePurchased}
      />
    );
  }

  // Onboarding wizard — overlays the dashboard for paid users with incomplete setup
  const wizardEl = showWizard && onboarding ? (
    <OnboardingWizard
      status={onboarding}
      billing={billing}
      onRefresh={async () => { await refreshOnboarding(); await fetchData?.(); }}
      onUpgrade={openPricing}
      onClose={() => setShowWizard(false)}
    />
  ) : null;

  return (
    <UsageProvider>
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Toaster
        position="top-right"
        toastOptions={{
          className: "bg-zinc-950 border border-white/10 text-zinc-100",
        }}
      />
      <UsageCapModal onUpgrade={openPricing} />
      <Sidebar
        active={activeView}
        onChange={setActiveView}
        connectedChannel={channels[0]}
        user={user}
        onSignOut={onSignOut}
        billing={billing}
        onUpgrade={openPricing}
        onManageBilling={openBillingPortal}
      />

      {/* Persistent connected-channel pill — top-right of the viewport on every
          screen so users always know which YouTube account is wired up. Clicking
          it pops the onboarding wizard back open (Step 1 = Switch account).
          Rendered as a sibling of <main> (NOT inside it) because <main> has the
          `.bg-canvas` class, which applies `> * { position: relative }` in
          App.css — that would override Tailwind's `fixed` and trap the pill
          inside the ml-64 main column, partially hidden behind the sidebar. */}
      {onboarding?.youtube_oauth_connected && onboarding?.youtube_channel_title && (
        <ChannelPillMenu
          onboarding={onboarding}
          heartbeat={heartbeat}
          onOpenWizard={() => setShowWizard(true)}
          onOpenTracking={() => setActiveView("tracking")}
          onDisconnect={async () => {
            try {
              await axios.delete(`${API}/youtube/oauth/disconnect`);
              toast.success("YouTube disconnected");
              await Promise.all([fetchData?.(), refreshOnboarding()]);
            } catch (e) {
              toast.error("Couldn't disconnect YouTube");
            }
          }}
        />
      )}

      <main className="ml-64 bg-canvas noise">
        <UsageBanner onUpgrade={openPricing} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-10 sm:py-12">
          {/* Free-user trial CTA — first thing a brand-new signup sees. Without
              this banner, free users land on a near-empty dashboard with no
              hint that a 14-day trial exists. Single click → Stripe checkout
              with the trial pre-applied (skips the pricing page entirely). */}
          {trialEligible && (
            <div
              className="w-full mb-6 flex items-center justify-between gap-4 px-5 py-4 rounded-xl border border-red-500/30 bg-gradient-to-r from-red-500/[0.08] to-red-500/[0.02] animate-fade-up"
              data-testid="free-trial-cta-banner"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-red-500/20 border border-red-500/40 text-red-400 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-white text-base font-semibold" data-testid="free-trial-cta-headline">
                    Start your 14-day free trial
                  </div>
                  <div className="text-sm text-zinc-400">
                    Unlock sales attribution, UTM injection, and pixel tracking. No charge for 14 days — cancel anytime.
                  </div>
                </div>
              </div>
              <Button
                onClick={startFreeTrial}
                className="bg-red-600 hover:bg-red-500 text-white font-semibold border border-red-400/40 shadow-[0_0_20px_rgba(255,0,0,0.35)] flex-shrink-0"
                data-testid="free-trial-cta-btn"
              >
                Start free trial <ArrowUpRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          )}

          {/* Trial status banner — sticky reminder for users currently on a
              Stripe trial. Combined with the email reminders, this captures the
              users who don't open emails but DO open the dashboard daily, which
              historically lifts trial → paid conversion well past 30%. */}
          {billing?.status === "trialing" && billing?.current_period_end && (() => {
            const trialEnd = new Date(billing.current_period_end);
            const msLeft = trialEnd.getTime() - Date.now();
            if (msLeft <= 0) return null; // trial already lapsed — Stripe will sync soon
            const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
            const urgent = daysLeft <= 1;
            const warning = daysLeft <= 3;
            const tone = urgent
              ? "border-red-500/35 bg-red-500/[0.08] text-red-300"
              : warning
                ? "border-amber-500/30 bg-amber-500/[0.06] text-amber-300"
                : "border-sky-500/25 bg-sky-500/[0.05] text-sky-300";
            const iconBg = urgent
              ? "bg-red-500/20 border-red-500/40 text-red-400"
              : warning
                ? "bg-amber-500/20 border-amber-500/40 text-amber-400"
                : "bg-sky-500/20 border-sky-500/40 text-sky-400";
            const headline = daysLeft === 1
              ? "Your VidROI trial ends tomorrow"
              : `Your VidROI trial ends in ${daysLeft} days`;
            return (
              <div
                className={`w-full mb-6 flex items-center justify-between gap-4 px-5 py-3 rounded-xl border animate-fade-up ${tone}`}
                data-testid="trial-status-banner"
                data-days-left={daysLeft}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                    <Clock className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-white text-sm font-medium truncate" data-testid="trial-status-headline">
                      {headline}
                    </div>
                    <div className="text-xs text-zinc-400 truncate">
                      Update your payment method now to keep your channels syncing without a break.
                    </div>
                  </div>
                </div>
                <Button
                  onClick={openBillingPortal}
                  className="bg-white text-zinc-900 hover:bg-zinc-100 font-medium flex-shrink-0"
                  data-testid="trial-status-manage-btn"
                >
                  Manage billing <ArrowUpRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            );
          })()}
          {/* "Finish setup" banner — appears once user dismisses the wizard without completing it */}
          {onboarding && !showWizard && billing?.plan && billing.plan !== "free" && !onboarding.completed && !(onboarding.youtube_oauth_connected && onboarding.has_offer && onboarding.pixel_installed) && (
            <button
              type="button"
              onClick={() => setShowWizard(true)}
              className="w-full mb-6 flex items-center justify-between gap-4 px-5 py-3 rounded-xl border border-red-500/25 bg-red-500/[0.05] hover:bg-red-500/[0.08] transition-colors text-left animate-fade-up"
              data-testid="resume-onboarding-banner"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-red-500/15 border border-red-500/30 text-red-400 flex items-center justify-center">
                  <Crosshair className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-white text-sm font-medium">Finish setup</div>
                  <div className="text-xs text-zinc-400">
                    {[
                      onboarding.youtube_oauth_connected,
                      onboarding.has_offer,
                      onboarding.pixel_installed,
                    ].filter(Boolean).length} of 3 steps complete — resume the setup wizard
                  </div>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-zinc-500" />
            </button>
          )}
          {activeView === "home" && (
            <DashboardHome
              user={user}
              channels={channels}
              videos={videos}
              onNavigate={setActiveView}
            />
          )}
          {activeView === "dashboard" && renderDashboard()}
          {activeView === "tracking" && renderTracking()}
          {activeView === "utm" && (
            <>
              {renderHeader("UTM", "Builder", "attribution links for every video", null)}
              <UtmBuilder settings={utmSettings} onChange={setUtmSettings} />
            </>
          )}
          {activeView === "competitors" && renderCompetitors()}
          {activeView === "swipe-file" && (
            <>
              {renderHeader("Your", "Swipe file", "hook library", null)}
              <SwipeFileScreen onAnalyzeCompetitor={() => setActiveView("competitors")} />
            </>
          )}
          {activeView === "channels" && renderChannels()}
          {activeView === "offers" && (
            <>
              {renderHeader("Your", "Offers", "products", null)}
              <OfferManager
                billing={billing}
                onUpgrade={openPricing}
                onTrial={startFreeTrial}
                trialEligible={trialEligible}
              />
            </>
          )}
          {activeView === "integrations" && (
            <>
              {renderHeader("Your", "Integrations", "webhooks & APIs", null)}
              <UsageSummaryCard onUpgrade={openPricing} />
              <IntegrationsScreen />
            </>
          )}
          {activeView === "admin" && user?.is_admin && (
            <AdminScreen />
          )}
          {activeView === "ask-ai" && (
            <AskAiScreen
              days={mapRangeToDays(timeRange).days ?? null}
              onUpgrade={openPricing}
            />
          )}
        </div>
        <SiteFooter variant="app" />
      </main>
      {wizardEl}
      <AskAiBubble
        days={mapRangeToDays(timeRange).days ?? null}
        onUpgrade={openPricing}
        currentView={activeView}
      />
    </div>
    </UsageProvider>
  );
}

// ===========================================================================
// Sub-components
// ===========================================================================

function StatCard({ icon: Icon, label, value, sub, delta, prevLabel, accent, testid }) {
  let deltaUI = null;
  if (delta !== undefined && delta !== null && Number.isFinite(delta)) {
    const up = delta > 0;
    const flat = delta === 0;
    const color = flat ? "text-zinc-500" : up ? "text-emerald-400" : "text-red-400";
    const Arrow = flat ? Minus : up ? ArrowUp : ArrowDown;
    deltaUI = (
      <div className={`flex items-center gap-1 text-xs font-medium ${color}`}>
        <Arrow className="w-3 h-3" />
        <span className="tabular-nums">{Math.abs(delta).toFixed(1)}%</span>
      </div>
    );
  } else if (delta === null) {
    deltaUI = <span className="text-xs text-zinc-600">no prior data</span>;
  }

  return (
    <div
      data-testid={testid}
      className={`relative overflow-hidden rounded-2xl border backdrop-blur-xl p-5 transition-all duration-300 animate-fade-up
        ${accent
          ? "bg-gradient-to-br from-red-500/[0.10] to-transparent border-red-500/25 shadow-[0_0_24px_-12px_rgba(255,0,0,0.5)]"
          : "bg-white/[0.02] border-white/[0.08] hover:bg-white/[0.04] hover:border-white/[0.15]"
        }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border
          ${accent ? "bg-red-500/15 text-red-400 border-red-500/30" : "bg-white/[0.04] text-zinc-400 border-white/10"}`}>
          <Icon className="w-4 h-4" />
        </div>
        {deltaUI}
      </div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-1">{label}</div>
      <div className="text-3xl font-bold tracking-tight text-white tabular-nums">{value}</div>
      {(sub || prevLabel) && <div className="text-xs text-zinc-500 mt-1">{sub || prevLabel}</div>}
    </div>
  );
}

function SectionPanel({ icon: Icon, title, subtitle, badge, children }) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl p-6 sm:p-8 animate-fade-up">
      <div className="flex items-start gap-4 mb-5">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-red-500/10 text-red-400 border border-red-500/25">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">{title}</h2>
            {badge && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] uppercase tracking-[0.18em] font-semibold"
                data-testid="section-panel-badge"
              >
                {badge}
              </span>
            )}
          </div>
          {subtitle && <p className="text-sm text-zinc-400 mt-1 max-w-2xl leading-relaxed">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function VideoTable(props) {
  // Moved to ./VideoTable.jsx — keep a thin re-export so the rest of the file
  // doesn't need to change. Component itself lives in its own file now.
  return <VideoTableImpl {...props} />;
}

function InstructionBox({ step, title, body, code }) {
  return (
    <div className="rounded-xl bg-white/[0.02] border border-white/10 p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-1">Step {step}</div>
      <div className="text-white font-medium mb-1.5">{title}</div>
      <div className="text-sm text-zinc-400">{body}</div>
      {code && (
        <code className="block mt-2 bg-black/40 border border-white/10 rounded-md px-3 py-1.5 text-xs text-red-300 font-mono">
          {code}
        </code>
      )}
    </div>
  );
}

function EmptyChannelCard({ onConnect }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 text-center animate-fade-up">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-red-500/10 border border-red-500/25 text-red-400 mb-4">
        <Youtube className="w-6 h-6" />
      </div>
      <h3 className="text-lg font-medium text-white">No channel connected yet</h3>
      <p className="text-sm text-zinc-400 mt-1 mb-5 max-w-md mx-auto">
        Connect your YouTube channel to see your at-a-glance stats, top videos, and start tracking sales.
      </p>
      <Button
        onClick={onConnect}
        className="bg-red-600 hover:bg-red-500 text-white shadow-[0_0_18px_rgba(255,0,0,0.35)] border border-red-400/30"
      >
        <Plus className="w-4 h-4 mr-1.5" /> Connect Channel
      </Button>
    </div>
  );
}

// ===========================================================================
// Top-level App — auth gate + session-id callback handler
// ===========================================================================

// Synchronously detect a session_id in the URL fragment so we can handle the
// Emergent OAuth callback BEFORE checking the existing session via /auth/me.
function extractSessionIdFromHash() {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash || "";
  const m = hash.match(/session_id=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

// Detect the public share path `/share/{slug}` synchronously during render
// so we never even hit the auth check on that route.
function extractShareSlug() {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/^\/share\/([a-z0-9]{4,32})\/?$/);
  return m ? m[1] : null;
}

// Public legal pages — must render without auth so Google's OAuth verification
// crawler can read them. Detected synchronously alongside the share route.
function extractLegalPage() {
  if (typeof window === "undefined") return null;
  const p = window.location.pathname.replace(/\/+$/, "");
  if (p === "/privacy") return "privacy";
  if (p === "/terms") return "terms";
  return null;
}

// Marketing homepage vs login route. Visitors at `/` see the marketing
// page (unless signed in — then they go straight to dashboard). The
// `/login` path goes straight to the Emergent Google OAuth sign-in.
function extractRoute() {
  if (typeof window === "undefined") return "marketing";
  const p = window.location.pathname.replace(/\/+$/, "");
  if (p === "/login") return "login";
  // Any other authenticated path (e.g. /dashboard, deep links) → app
  if (p === "" || p === "/") return "root";
  return "app";
}

export default function App() {
  // Public share viewer — completely unauthenticated, runs before the auth gate.
  const shareSlug = extractShareSlug();
  if (shareSlug) {
    return <SharedHookPage slug={shareSlug} />;
  }
  // Public legal pages — also unauth so reviewers / search crawlers see them.
  const legal = extractLegalPage();
  if (legal === "privacy") return <PrivacyPage />;
  if (legal === "terms") return <TermsPage />;
  return <AuthGate />;
}

function AuthGate() {
  // auth states: null = checking, false = logged out, object = logged in
  const [authUser, setAuthUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const callbackRan = useRef(false);

  useEffect(() => {
    const pendingSessionId = extractSessionIdFromHash();

    async function run() {
      // 1) Handle OAuth callback (?#session_id=...)
      if (pendingSessionId && !callbackRan.current) {
        callbackRan.current = true;
        try {
          const { data } = await axios.post(`${API}/auth/session`, {
            session_id: pendingSessionId,
          });
          // Strip the hash so a reload doesn't re-trigger the exchange
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          setAuthUser(data.user || null);
          setAuthChecked(true);
          return;
        } catch (e) {
          console.error("Session exchange failed", e);
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          // fall through to /auth/me check
        }
      }

      // 2) Check existing session
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        setAuthUser(data);
      } catch {
        setAuthUser(false);
      } finally {
        setAuthChecked(true);
      }
    }

    run();
  }, []);

  const handleSignOut = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Even if the server-side logout fails (network), we still clear local state below
      console.warn("sign-out request failed:", err?.message || err);
    }
    // Defensive: wipe every VidROI localStorage entry on sign-out so the
    // next user on the same browser can't see leftover offer URLs, UTM
    // templates, or per-video overrides. The per-user-scoped keys also
    // protect against this, but a hard sweep is the right safety net.
    try {
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (k.startsWith("utm_builder_settings_v1") || k.startsWith("utm_overrides_v1"))) {
          toRemove.push(k);
        }
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch (err) {
      console.warn("localStorage cleanup on sign-out failed (non-fatal):", err?.message || err);
    }
    setAuthUser(false);
    // Hard reload to the marketing page. This guarantees:
    //   1. Any cached dashboard JSX is dropped from memory.
    //   2. The next mount's /auth/me check runs cleanly against a fresh
    //      cookie state (the server-side cookie should already be cleared,
    //      but a reload removes any chance of a stale request in-flight
    //      still resolving as authenticated).
    //   3. Open WebSocket / SSE / long-poll handles are torn down.
    // Without this, "I logged out and still see the dashboard" was possible
    // when the browser repainted from cached React state before the auth
    // check could 401 the next API call.
    if (typeof window !== "undefined") {
      window.location.replace("/");
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-7 h-7 animate-spin text-red-500 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Signing you in…</p>
        </div>
      </div>
    );
  }

  if (!authUser) {
    // `/login` always shows the Google sign-in screen. Any other path
    // (including `/`) shows the marketing homepage. The hero/nav buttons
    // on the marketing page point at `/login` so visitors who're ready
    // to convert land directly on the OAuth screen.
    const route = extractRoute();
    if (route === "login") return <Login />;
    return <MarketingHomepage />;
  }

  return <Dashboard user={authUser} onSignOut={handleSignOut} />;
}

// ----------------------------------------------------------------------------
// ChannelPillMenu — the top-right YouTube channel pill + its quick-action
// popover. Rendered as a sibling of <main> (NOT a child) so the
// `.bg-canvas > * { position: relative }` rule in App.css doesn't trap the
// fixed-positioned pill inside the ml-64 main column. See the longer comment
// next to where it's instantiated.
// ----------------------------------------------------------------------------
function formatRelative(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  return `${diffD}d ago`;
}

function ChannelPillMenu({ onboarding, heartbeat, onOpenWizard, onOpenTracking, onDisconnect }) {
  const [open, setOpen] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const handleDisconnect = async () => {
    setConfirmingDisconnect(false);
    setOpen(false);
    await onDisconnect();
  };

  const handleOpenTracking = () => {
    setOpen(false);
    onOpenTracking();
  };

  const handleOpenWizard = () => {
    setOpen(false);
    onOpenWizard();
  };

  const liveTooltip = heartbeat?.live
    ? `Live · ${heartbeat.clicks_5m || 0} click${(heartbeat.clicks_5m || 0) === 1 ? "" : "s"} + ${heartbeat.sales_5m || 0} sale${(heartbeat.sales_5m || 0) === 1 ? "" : "s"} in the last 5 min — click for quick actions`
    : "Manage YouTube connection";

  const lastSaleRel = formatRelative(heartbeat?.last_sale_ts);
  const lastSaleAmount = typeof heartbeat?.last_sale_amount === "number"
    ? `$${heartbeat.last_sale_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="fixed top-4 right-6 z-30 flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border border-white/10 bg-zinc-950/80 backdrop-blur-md hover:bg-zinc-900/80 hover:border-red-500/30 transition-colors shadow-[0_4px_24px_-8px_rgba(0,0,0,0.6)] text-left max-w-[260px]"
          data-testid="header-channel-pill"
          title={liveTooltip}
        >
          <div className="relative flex-shrink-0">
            {onboarding.youtube_channel_thumbnail ? (
              <img
                src={onboarding.youtube_channel_thumbnail}
                alt={onboarding.youtube_channel_title}
                className="w-7 h-7 rounded-full object-cover border border-white/10"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 flex items-center justify-center text-[11px] font-semibold">
                {onboarding.youtube_channel_title.slice(0, 1).toUpperCase()}
              </div>
            )}
            {heartbeat?.live && (
              <span
                className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
                data-testid="header-channel-pill-live-dot"
                aria-label="Live tracking"
              >
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 border border-emerald-300/60 shadow-[0_0_8px_rgba(16,185,129,0.7)]" />
              </span>
            )}
          </div>
          <div className="min-w-0 leading-tight">
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold flex items-center gap-1.5">
              YouTube
              {heartbeat?.live && (
                <span
                  className="text-[9px] text-emerald-400 font-semibold tracking-[0.2em]"
                  data-testid="header-channel-pill-live-label"
                >
                  · LIVE
                </span>
              )}
            </div>
            <div className="text-[12px] text-white font-medium truncate" data-testid="header-channel-pill-name">
              {onboarding.youtube_channel_title}
            </div>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-72 p-0 bg-zinc-950/95 border-white/10 backdrop-blur-xl text-zinc-100 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.8)]"
        data-testid="channel-pill-menu"
      >
        {/* Status header — live counts + last sale */}
        <div className="px-4 pt-4 pb-3 border-b border-white/10">
          <div className="flex items-center gap-2 mb-2">
            {heartbeat?.live ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-400 font-semibold" data-testid="pill-menu-live-status">
                  Live tracking
                </span>
              </>
            ) : (
              <>
                <span className="inline-flex h-2 w-2 rounded-full bg-zinc-600" />
                <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold" data-testid="pill-menu-idle-status">
                  No activity in last 5 min
                </span>
              </>
            )}
          </div>
          {heartbeat?.live && (
            <div className="text-[12px] text-zinc-400" data-testid="pill-menu-live-detail">
              {heartbeat.clicks_5m || 0} click{(heartbeat.clicks_5m || 0) === 1 ? "" : "s"} · {heartbeat.sales_5m || 0} sale{(heartbeat.sales_5m || 0) === 1 ? "" : "s"} in the last 5 min
            </div>
          )}
          {lastSaleAmount && lastSaleRel && (
            <div className="mt-2 flex items-center gap-1.5 text-[12px]" data-testid="pill-menu-last-sale">
              <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-zinc-300">Last sale:</span>
              <span className="text-white font-medium">{lastSaleAmount}</span>
              <span className="text-zinc-500">· {lastSaleRel}</span>
            </div>
          )}
          {!lastSaleAmount && (
            <div className="mt-2 text-[12px] text-zinc-500" data-testid="pill-menu-no-sales">
              No sales attributed yet.
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="py-1.5">
          <button
            type="button"
            onClick={handleOpenTracking}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-200 hover:bg-white/[0.05] hover:text-white transition-colors text-left"
            data-testid="pill-menu-open-tracking"
          >
            <CrosshairIcon className="w-4 h-4 text-zinc-500" />
            Open Tracking tab
          </button>
          <button
            type="button"
            onClick={handleOpenWizard}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-200 hover:bg-white/[0.05] hover:text-white transition-colors text-left"
            data-testid="pill-menu-switch-account"
          >
            <RefreshCw className="w-4 h-4 text-zinc-500" />
            Switch account
          </button>
          {!confirmingDisconnect ? (
            <button
              type="button"
              onClick={() => setConfirmingDisconnect(true)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-400 hover:bg-red-500/10 hover:text-red-400 transition-colors text-left border-t border-white/5 mt-1.5 pt-2.5"
              data-testid="pill-menu-disconnect"
            >
              <DisconnectIcon className="w-4 h-4" />
              Disconnect YouTube
            </button>
          ) : (
            <div className="px-4 pt-3 pb-3 mt-1.5 border-t border-white/5 bg-red-500/[0.04]" data-testid="pill-menu-disconnect-confirm">
              <div className="text-[12px] text-zinc-300 mb-2">Disconnect this YouTube account?</div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmingDisconnect(false)}
                  className="flex-1 bg-white/[0.03] border-white/10 text-zinc-300 hover:bg-white/[0.08] hover:text-white"
                  data-testid="pill-menu-disconnect-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleDisconnect}
                  className="flex-1 bg-red-600 hover:bg-red-500 text-white border border-red-400/30"
                  data-testid="pill-menu-disconnect-confirm-btn"
                >
                  Yes, disconnect
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

